import { useState, useMemo } from 'react';
import { useList, useUpdate, useCreate, useDelete, useInvalidate } from '@refinedev/core';
import { Plus, Check, Trash2, ToggleLeft, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FormHeader } from '@/components/forms';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { notify } from '@/lib/notify';
import { useConfirm } from '@/hooks/use-confirm';
import { DataTable, TableToolbar, FilterSelect, type Column, type SortState, type BulkAction } from '@/components/tables';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

type Currency = {
  id: string;
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  isActive: boolean;
  order: number;
};

/**
 * ISO 4217 currency reference
 * Популярные валюты с названиями, символами и десятичными знаками
 */
const ISO_CURRENCIES: Array<{ code: string; name: string; symbol: string; decimalPlaces: number }> = [
  { code: 'USD', name: 'US Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'EUR', name: 'Euro', symbol: '€', decimalPlaces: 2 },
  { code: 'GBP', name: 'British Pound', symbol: '£', decimalPlaces: 2 },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', decimalPlaces: 2 },
  { code: 'UZS', name: 'Uzbekistani Som', symbol: 'сўм', decimalPlaces: 0 },
  { code: 'KZT', name: 'Kazakhstani Tenge', symbol: '₸', decimalPlaces: 0 },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', decimalPlaces: 2 },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', decimalPlaces: 2 },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', decimalPlaces: 2 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', decimalPlaces: 2 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', decimalPlaces: 0 },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', decimalPlaces: 0 },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', decimalPlaces: 2 },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', decimalPlaces: 2 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', decimalPlaces: 2 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', decimalPlaces: 2 },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimalPlaces: 2 },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', decimalPlaces: 2 },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimalPlaces: 2 },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimalPlaces: 2 },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', decimalPlaces: 2 },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', decimalPlaces: 2 },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', decimalPlaces: 0 },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei', decimalPlaces: 2 },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', decimalPlaces: 2 },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾', decimalPlaces: 2 },
  { code: 'AZN', name: 'Azerbaijani Manat', symbol: '₼', decimalPlaces: 2 },
  { code: 'AMD', name: 'Armenian Dram', symbol: '֏', decimalPlaces: 0 },
  { code: 'BYN', name: 'Belarusian Ruble', symbol: 'Br', decimalPlaces: 2 },
  { code: 'KGS', name: 'Kyrgyzstani Som', symbol: 'сом', decimalPlaces: 2 },
  { code: 'TJS', name: 'Tajikistani Somoni', symbol: 'SM', decimalPlaces: 2 },
  { code: 'TMT', name: 'Turkmenistani Manat', symbol: 'T', decimalPlaces: 2 },
  { code: 'MDL', name: 'Moldovan Leu', symbol: 'L', decimalPlaces: 2 },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', decimalPlaces: 2 },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', decimalPlaces: 2 },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', decimalPlaces: 2 },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', decimalPlaces: 2 },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', decimalPlaces: 2 },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', decimalPlaces: 0 },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', decimalPlaces: 2 },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', decimalPlaces: 0 },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', decimalPlaces: 2 },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', decimalPlaces: 2 },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', decimalPlaces: 2 },
  { code: 'ISK', name: 'Icelandic Króna', symbol: 'kr', decimalPlaces: 0 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', decimalPlaces: 2 },
  { code: 'QAR', name: 'Qatari Riyal', symbol: '﷼', decimalPlaces: 2 },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', decimalPlaces: 2 },
];

function compareCurrencies(a: Currency, b: Currency, sort: SortState): number {
  const field = sort.field as keyof Currency;
  const aVal = a[field];
  const bVal = b[field];
  let cmp = 0;
  if (typeof aVal === 'string' && typeof bVal === 'string') {
    cmp = aVal.localeCompare(bVal, 'ru');
  } else if (typeof aVal === 'number' && typeof bVal === 'number') {
    cmp = aVal - bVal;
  } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
    cmp = Number(aVal) - Number(bVal);
  }
  return sort.order === 'desc' ? -cmp : cmp;
}

export function CurrenciesList() {
  useScrollRestoration();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [search, setSearch] = usePersistedState('currencies:search', '');
  const [activeFilter, setActiveFilter] = usePersistedState<'all' | 'active' | 'inactive'>('currencies:activeFilter', 'all');
  const [sort, setSort] = usePersistedState<SortState | undefined>('currencies:sort', { field: 'order', order: 'asc' });
  const { confirm, ConfirmationDialog } = useConfirm();

  const listResult = useList<Currency>({
    resource: 'admin/currencies',
    pagination: { currentPage: 1, pageSize: 200, mode: 'server' },
  });

  const rawData = listResult.query.data?.data;
  const isLoading = listResult.query.isLoading;

  const { mutate: updateCurrency } = useUpdate();
  const { mutate: createCurrency } = useCreate();
  const { mutateAsync: deleteCurrencyAsync } = useDelete();
  const invalidate = useInvalidate();

  const allCurrencies = useMemo(
    () => (rawData ?? []) as Currency[],
    [rawData],
  );

  const existingCodes = useMemo(() => new Set(allCurrencies.map((c) => c.code)), [allCurrencies]);

  // Client-side filtering
  const filtered = useMemo(() => {
    let result = allCurrencies;
    if (activeFilter === 'active') {
      result = result.filter((c) => c.isActive);
    } else if (activeFilter === 'inactive') {
      result = result.filter((c) => !c.isActive);
    }
    const q = search.trim().toLowerCase();
    if (!q) { return result; }
    return result.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q),
    );
  }, [allCurrencies, search, activeFilter]);

  // Client-side sorting
  const sorted = useMemo(() => {
    if (!sort) { return filtered; }
    return [...filtered].sort((a, b) => compareCurrencies(a, b, sort));
  }, [filtered, sort]);

  const handleToggleActive = (currencies: Currency[], active: boolean) => {
    let completed = 0;
    for (const currency of currencies) {
      updateCurrency({
        resource: 'admin/currencies',
        id: currency.code,
        values: { isActive: active },
        successNotification: false,
        invalidates: [],
      }, {
        onSuccess: () => {
          completed++;
          if (completed === currencies.length) {
            invalidate({ resource: 'admin/currencies', invalidates: ['list'] });
            invalidate({ resource: 'currencies', invalidates: ['list'] });
            notify.success(active ? `Включено: ${currencies.length}` : `Выключено: ${currencies.length}`);
          }
        },
        onError: () => {
          notify.error('Ошибка изменения статуса');
        },
      });
    }
  };

  const handleDelete = async (currencies: Currency[]) => {
    const confirmed = await confirm({
      title: currencies.length === 1 ? `Удалить валюту ${currencies[0].code}?` : `Удалить ${currencies.length} валют?`,
      description: 'Это действие нельзя отменить.',
      confirmText: 'Удалить',
      variant: 'destructive',
    });
    if (!confirmed) { return; }
    try {
      for (const currency of currencies) {
        await deleteCurrencyAsync({ resource: 'admin/currencies', id: currency.code, successNotification: false, invalidates: [] });
      }
      notify.success(currencies.length === 1 ? 'Валюта удалена' : `Удалено валют: ${currencies.length}`);
      invalidate({ resource: 'admin/currencies', invalidates: ['list'] });
      invalidate({ resource: 'currencies', invalidates: ['list'] });
    } catch {
      notify.error('Ошибка удаления');
    }
  };

  const toggleSelection = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code)
        ? prev.filter((c) => c !== code)
        : [...prev, code]
    );
  };

  const handleAddCurrencies = () => {
    if (selectedCodes.length === 0) { return; }

    const toAdd = selectedCodes
      .map((code) => ISO_CURRENCIES.find((c) => c.code === code))
      .filter((iso): iso is NonNullable<typeof iso> => !!iso);

    if (toAdd.length === 0) { return; }

    let completed = 0;
    for (let i = 0; i < toAdd.length; i++) {
      const iso = toAdd[i];
      createCurrency({
        resource: 'admin/currencies',
        values: {
          code: iso.code,
          name: iso.name,
          symbol: iso.symbol,
          decimalPlaces: iso.decimalPlaces,
          isActive: true,
          order: allCurrencies.length + i,
        },
        successNotification: false,
        invalidates: [],
      }, {
        onSuccess: () => {
          completed++;
          if (completed === toAdd.length) {
            invalidate({ resource: 'admin/currencies', invalidates: ['list'] });
            invalidate({ resource: 'currencies', invalidates: ['list'] });
            notify.success(`Добавлено валют: ${toAdd.length}`);
          }
        },
      });
    }

    setSelectedCodes([]);
    setDialogOpen(false);
  };

  const availableCurrencies = ISO_CURRENCIES.filter((c) => !existingCodes.has(c.code));

  const columns: Column<Currency>[] = [
    {
      key: 'code',
      header: 'Код',
      sortable: true,
      width: '90px',
      render: (_, item) => (
        <Badge variant="outline" className="font-mono text-sm w-14 justify-center">
          {item.code}
        </Badge>
      ),
    },
    {
      key: 'symbol',
      header: 'Символ',
      width: '70px',
      render: (_, item) => (
        <span className="text-lg text-center block">{item.symbol}</span>
      ),
    },
    {
      key: 'name',
      header: 'Название',
      sortable: true,
      render: (_, item) => <span className="font-medium">{item.name}</span>,
    },
    {
      key: 'decimalPlaces',
      header: 'Знаков',
      sortable: true,
      width: '90px',
      render: (_, item) => <span className="text-sm text-muted-foreground">{item.decimalPlaces}</span>,
    },
    {
      key: 'order',
      header: 'Порядок',
      sortable: true,
      width: '100px',
      render: (_, item) => <span className="text-sm text-muted-foreground">{item.order}</span>,
    },
    {
      key: 'isActive',
      header: 'Статус',
      sortable: true,
      width: '110px',
      render: (_, item) => (
        <Badge variant={item.isActive ? 'default' : 'outline'}>
          {item.isActive ? 'Активна' : 'Неактивна'}
        </Badge>
      ),
    },
  ];

  const bulkActions: BulkAction<Currency>[] = [
    {
      label: 'Редактировать',
      icon: <Pencil className="h-4 w-4" />,
      showOnlyWhenSelected: true,
      onClick: (items) => {
        if (items.length !== 1) {
          notify.info('Для редактирования выберите одну валюту');
          return;
        }
        // Inline toggle active for now (no edit page for currencies)
        notify.info(`Редактирование валюты ${items[0].code} — откройте форму`);
      },
    },
    {
      label: 'Включить',
      icon: <ToggleLeft className="h-4 w-4" />,
      showOnlyWhenSelected: true,
      onClick: (items) => handleToggleActive(items, true),
    },
    {
      label: 'Выключить',
      icon: <ToggleLeft className="h-4 w-4" />,
      variant: 'secondary',
      showOnlyWhenSelected: true,
      onClick: (items) => handleToggleActive(items, false),
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      showOnlyWhenSelected: true,
      onClick: (items) => { void handleDelete(items); },
    },
  ];

  return (
    <div className="space-y-6">
      {ConfirmationDialog}

      <FormHeader
        title="Валюты"
        description="Управление валютами, доступными в системе"
        sticky
        actions={
          <Button onClick={() => setDialogOpen(true)} disabled={availableCurrencies.length === 0}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить валюту
          </Button>
        }
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(value) => setSearch(value)}
        searchPlaceholder="Поиск по коду, названию, символу..."
        filtersCount={activeFilter !== 'all' ? 1 : 0}
        onResetFilters={() => setActiveFilter('all')}
        filters={
          <FilterSelect
            label="Статус"
            value={activeFilter}
            onChange={(v) => setActiveFilter(v as 'all' | 'active' | 'inactive')}
            options={[
              { value: 'active', label: 'Активные' },
              { value: 'inactive', label: 'Неактивные' },
            ]}
            allLabel="Все"
          />
        }
      />

      <DataTable
        columns={columns}
        data={sorted}
        total={sorted.length}
        page={1}
        pageSize={sorted.length || 1}
        isLoading={isLoading}
        onPageChange={() => {}}
        sort={sort}
        onSortChange={setSort}
        emptyMessage="Нет валют в системе. Нажмите «Добавить валюту» чтобы начать."
        rowKey="code"
        bulkActions={bulkActions}
      />

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setSelectedCodes([]); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Добавить валюты</DialogTitle>
          </DialogHeader>
          <Command className="border rounded-lg">
            <CommandInput placeholder="Поиск валюты..." />
            <CommandList className="max-h-72">
              <CommandEmpty>Ничего не найдено</CommandEmpty>
              <CommandGroup>
                {availableCurrencies.map((currency) => {
                  const isSelected = selectedCodes.includes(currency.code);
                  return (
                    <CommandItem
                      key={currency.code}
                      value={`${currency.code} ${currency.name}`}
                      onSelect={() => toggleSelection(currency.code)}
                      className="cursor-pointer"
                    >
                      <div className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                        isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30'
                      )}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <Badge variant="outline" className="font-mono text-xs mr-2">
                        {currency.code}
                      </Badge>
                      <span className="mr-2">{currency.symbol}</span>
                      <span>{currency.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-muted-foreground">
              {selectedCodes.length > 0 ? `Выбрано: ${selectedCodes.length}` : 'Выберите валюты'}
            </span>
            <Button
              onClick={handleAddCurrencies}
              disabled={selectedCodes.length === 0}
            >
              Добавить {selectedCodes.length > 0 && `(${selectedCodes.length})`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
